import React, { memo, useMemo } from 'react';

import { useImageSourceProps } from '../hooks';
import { Image } from '../Image';
import { getContentfulImageSources } from './getContentfulImageSources';
import { getContentfulImageUrl } from './getContentfulImageUrl';

import type { ImageProps } from '../Image.types';

// ─────────────────────────────────────────────────────────────────────────────

export const ContentfulImage = memo((props: ContentfulImageProps) => {
  const { baseUrl, options, staticBaseUrl, ...rest } = props;

  // ─── Derived Data ────────────────────────────────────────────────────

  const sources = useMemo(() => {
    return getContentfulImageSources(baseUrl, options);
  }, [baseUrl, options]);

  const staticSourceUrl = useMemo(() => {
    return staticBaseUrl ? getContentfulImageUrl(staticBaseUrl, options) : undefined;
  }, [staticBaseUrl, options]);

  const imageSourceProps = useImageSourceProps({
    source: sources,
    staticSourceUrl,
  });

  // ─────────────────────────────────────────────────────────────────────

  return <Image {...imageSourceProps} {...rest} />;
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ContentfulImageProps = Readonly<{
  baseUrl: string;

  /**
   * A URL to the optional static image that can be used for a11y purposes to
   * swap animated image with a static version based on the "reduce motion" mode
   * on native platform or the "static source" attribute on the web.
   *
   * @see {@link useStaticImageProps}
   */
  staticBaseUrl?: string;

  options: Parameters<typeof getContentfulImageSources>[1];
}> &
  Omit<ImageProps, 'source'>;
